import { Box, Checkbox, Chip, FormControlLabel, InputAdornment, Stack, TextField, alpha, useTheme } from "@mui/material";
import StepHeader from "./StepHeader";
import FrostedCard from "./FrostedCard";
import WizardEyebrow from "./WizardEyebrow";
import { isZeroValue } from "src/utils/quote/quoteValidation";
import { ResultRow } from "src/components/quote/AddressFeasibilityPanel";
import { localizeDecimal } from "src/utils/valut/valutFormatters";
import { useLang, useT, useTRich } from "src/i18n";

const PUOMI_PRESETS = [24, 27, 33, 36, 47];
const LINJA_PRESETS = [10, 50];

// Step 2 — "Pumppu"
// Two distance inputs drive the request: how far the boom reaches, and how
// much extra line is run past the boom tip. The checkboxes are quick-fill
// shortcuts that set the corresponding value to "0" — they are a *consequence*
// of the number, so they live under the field, smaller and dimmer.
//
// Data model is unchanged: `puomi` and `linjanPituus` (string m). 0 means
// "no boom" / "no extension line" respectively.
export default function StepPump({ draft, errors = {}, onChange, boomCheck }) {
    const theme = useTheme();
    const w = theme.custom?.wizard ?? {};
    const amber = w.amber ?? "#E8893B";
    const t = useT();
    const tRich = useTRich();
    const { lang } = useLang();

    const update = (key) => (e) => onChange({ [key]: e.target.value });
    const isPerasta = isZeroValue(draft.puomi);
    const isRoikosta = isZeroValue(draft.linjanPituus);
    const bothZero = isPerasta && isRoikosta;
    // Both distances left blank (not an explicit 0) — a legitimate "I don't know"
    // for a first-timer. Reassure that it's OK to skip; the provider assesses.
    const bothEmpty = !draft.puomi && !draft.linjanPituus;

    // Live total — only show when at least one value is a positive number.
    // Rounded to 1 decimal to avoid 0.1 + 0.2 = 0.30000000000000004 ugliness.
    const puomiNum = Number(draft.puomi);
    const linjaNum = Number(draft.linjanPituus);
    const rawTotal =
        (Number.isFinite(puomiNum) ? puomiNum : 0) +
        (Number.isFinite(linjaNum) ? linjaNum : 0);
    const total = Math.round(rawTotal * 10) / 10;
    const showTotal = total > 0;

    // Amber is the one lamp (DESIGN.md) — reserved for the selected chip only.
    // Unselected chips get a neutral outline, so selection reads as a clear
    // signal rather than every option glowing identically. height:44 meets the
    // WCAG touch-target floor (was 26px).
    const chipSx = (active) => ({
        fontFamily: "'Inter', sans-serif",
        fontSize: 12,
        fontWeight: 600,
        height: 44,
        borderRadius: "999px",
        bgcolor: active ? alpha(amber, 0.22) : "transparent",
        color: active ? amber : "text.secondary",
        border: active
            ? `1px solid ${alpha(amber, 0.55)}`
            : `1px solid ${alpha(theme.palette.text.secondary, 0.35)}`,
        cursor: "pointer",
        transition: "background-color 120ms, border-color 120ms, color 120ms",
        "&:hover": {
            bgcolor: active ? alpha(amber, 0.28) : alpha(theme.palette.text.secondary, 0.08),
            borderColor: active ? alpha(amber, 0.65) : alpha(theme.palette.text.secondary, 0.55),
        },
        "& .MuiChip-label": { px: 1.25 },
    });

    function renderPresetChips(field, presets) {
        const current = String(draft[field] ?? "");
        return (
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                mt: 1,
                pl: { xs: 0.5, sm: 1 },
            }}>
                {presets.map((m) => (
                    <Chip
                        key={m}
                        label={`${m} m`}
                        size="small"
                        onClick={() => onChange({ [field]: String(m) })}
                        sx={chipSx(current === String(m))}
                    />
                ))}
            </Box>
        );
    }

    function handlePerastaToggle(e) {
        onChange({ puomi: e.target.checked ? "0" : "" });
    }

    function handleRoikostaToggle(e) {
        onChange({ linjanPituus: e.target.checked ? "0" : "" });
    }

    return (
        <Stack spacing={2}>
            <StepHeader>{t("wizard.stepPump.title")}</StepHeader>

            <Box sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "1.05rem", sm: "1.15rem" },
                fontWeight: 600,
                color: "text.primary",
                maxWidth: "60ch",
                lineHeight: 1.4,
            }}>
                {t("wizard.stepPump.heading")}
            </Box>
            <Box sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.95rem",
                color: "text.secondary",
                maxWidth: "60ch",
                lineHeight: 1.55,
            }}>
                {tRich("wizard.stepPump.intro", {
                    boom: <strong>{t("wizard.stepPump.boomWord")}</strong>,
                    linja: <strong>{t("wizard.stepPump.linjaWord")}</strong>,
                })}
            </Box>

            {/* PUOMI — boom reach */}
            <FrostedCard
                tone={bothZero ? "error" : "neutral"}
                sx={{ p: { xs: 2, sm: 2.5 } }}
            >
                <WizardEyebrow color="text.secondary" sx={{ mb: 1 }}>
                    {t("wizard.stepPump.boomSectionTitle")}
                </WizardEyebrow>
                <TextField
                    label={t("wizard.stepPump.boomFieldLabel")}
                    type="number"
                    fullWidth
                    value={draft.puomi}
                    onChange={update("puomi")}
                    slotProps={{
                        htmlInput: { min: 0, step: 1, "aria-label": t("wizard.stepPump.boomAriaLabel") },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Box sx={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "text.secondary",
                                        letterSpacing: "0.02em",
                                    }}>
                                        m
                                    </Box>
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={bothZero}
                    helperText={bothZero ? undefined : t("wizard.stepPump.boomHelper")}
                />
                {renderPresetChips("puomi", PUOMI_PRESETS)}
                <Box sx={{ pl: { xs: 1, sm: 1.5 }, mt: 0.25 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={isPerasta}
                                onChange={handlePerastaToggle}
                                sx={{ p: 1.25 }}
                            />
                        }
                        label={
                            <Box component="span" sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: 13,
                                color: "text.secondary",
                                lineHeight: 1.4,
                            }}>
                                {t("wizard.stepPump.noBoomLabel")}
                            </Box>
                        }
                        sx={{ m: 0, alignItems: "flex-start" }}
                    />
                </Box>
            </FrostedCard>

            {/* Live boom capability count — fed by RequestForm's debounced
                checkAddress(requiredPuomi) pass. Row only renders when the
                result matches the current puomi value (forPuomi guard). */}
            {boomCheck?.status === "done" && puomiNum > 0 && boomCheck.forPuomi === puomiNum && (
                boomCheck.providerCount > 0 ? (
                    <ResultRow
                        tone="success"
                        title={t("wizard.stepPump.boomResultTitle")}
                        body={t("wizard.stepPump.boomResultBody", {
                            count: boomCheck.providerCount,
                            unit: t(boomCheck.providerCount === 1 ? "wizard.units.pumppufirmaOne" : "wizard.units.pumppufirmaOther"),
                        })}
                    />
                ) : (
                    <ResultRow
                        tone="warn"
                        title={t("wizard.stepPump.boomNoneTitle")}
                        body={t("wizard.stepPump.boomNoneBody")}
                    />
                )
            )}

            {/* LINJA — extension line past the boom tip */}
            <FrostedCard
                tone={bothZero ? "error" : "neutral"}
                sx={{ p: { xs: 2, sm: 2.5 } }}
            >
                <WizardEyebrow color="text.secondary" sx={{ mb: 1 }}>
                    {t("wizard.stepPump.linjaLabel")}
                </WizardEyebrow>
                <TextField
                    label={t("wizard.stepPump.linjaLabel")}
                    type="number"
                    fullWidth
                    value={draft.linjanPituus}
                    onChange={update("linjanPituus")}
                    slotProps={{
                        htmlInput: { min: 0, step: 1, "aria-label": t("wizard.stepPump.linjaAriaLabel") },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Box sx={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "text.secondary",
                                        letterSpacing: "0.02em",
                                    }}>
                                        m
                                    </Box>
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={bothZero}
                    helperText={
                        bothZero
                            ? (errors.puomi ? t(errors.puomi) : t("wizard.validation.pumpBothZero"))
                            : t("wizard.stepPump.linjaHelper")
                    }
                />
                {renderPresetChips("linjanPituus", LINJA_PRESETS)}
                <Box sx={{ pl: { xs: 1, sm: 1.5 }, mt: 0.25 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={isRoikosta}
                                onChange={handleRoikostaToggle}
                                sx={{ p: 1.25 }}
                            />
                        }
                        label={
                            <Box component="span" sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: 13,
                                color: "text.secondary",
                                lineHeight: 1.4,
                            }}>
                                {t("wizard.stepPump.noLinjaLabel")}
                                <Box component="span" sx={{ opacity: 0.7, ml: 0.5 }}>
                                    {t("wizard.stepPump.roikostaSuffix")}
                                </Box>
                            </Box>
                        }
                        sx={{ m: 0, alignItems: "flex-start" }}
                    />
                </Box>
            </FrostedCard>

            {bothEmpty && (
                <Box sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    color: "text.secondary",
                    lineHeight: 1.55,
                    maxWidth: "60ch",
                }}>
                    {t("wizard.stepPump.bothEmptyNote")}
                </Box>
            )}

            {showTotal && (
                <Box sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "flex-end",
                    gap: 1,
                    pr: 0.5,
                    fontFamily: "'Inter', sans-serif",
                }}>
                    <Box component="span" sx={{
                        fontSize: 12,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "text.secondary",
                    }}>
                        {t("wizard.stepPump.totalLabel")}
                    </Box>
                    <Box component="span" sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: amber,
                        letterSpacing: "-0.01em",
                    }}>
                        {localizeDecimal(total, lang)} m
                    </Box>
                </Box>
            )}
        </Stack>
    );
}
